import { MAX_ADDITIONAL_DOCUMENTS } from '@common/constants';
import type { ProspectMatterType, ProspectPracticeArea } from '@common/enums';
import type { ApsOcrExtraction } from '@common/types/apsOcr';
import type { DraftRetainerPreviewParams } from '@common/types/retainerPreview';
import { getApsClients } from '@common/utils/apsOcrPrefill';
import { getSigningName, formatAllClientNames } from '@common/utils/contactNames';
import {
    buildLegalDocPrefillsFromValues,
    getEditableLegalDocTags,
    getLegalDocPrefillLabel,
    getLegalDocPrefillWidget,
    getLegalDocValueKey,
    isValidLegalDocPrefillValue,
} from '@common/utils/legalDocPrefills';
import { APS_MATTER_FIELDS } from '@common/utils/matterFields';
import {
    applyFieldValuesToExtraction,
    getApsFieldValues,
    mergeScanFieldValues,
} from '@common/utils/prospectFieldValues';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AlertDialog from '@/components/AlertDialog';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { THIN_SCROLLBAR_STYLING } from '@/constants';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useLawFirm } from '@/queries/lawFirms';
import { useRetainerAgreements } from '@/queries/legalDocuments';
import {
    intakeEmailReminderTemplateQueryOptions,
    intakeEmailTemplateQueryOptions,
    quoteTemplateAttachmentsQueryOptions,
    quoteTemplateQueryOptions,
    useTemplates,
} from '@/queries/templates';
import { useUsers } from '@/queries/users';
import { createProspectManually } from '@/services/ProspectService';
import useApsDiscoveryStore from '@/stores/useApsDiscoveryStore';
import { errorSnackbar } from '@/utils/snackbar';
import ApsOcrScan from './ApsOcrScan';
import ClientDetails from './ClientDetails';
import {
    type ClientDetailsEntry,
    type ClientDetailsErrors,
    createClientDetailsEntry,
    getClientDetailsErrors,
    hasClientDetailsErrors,
    toCreateContact,
    withDefaultIdvSendTiming,
} from './clientEntries';
import EmailConfiguration from './EmailConfiguration';
import NewProspectAnimatedStep from './NewProspectAnimatedStep';
import NpInitialDetails from './NpInitialDetails';
import RetainerAgreement from './RetainerAgreement';
import type { UploadedDocument } from './RetainerAgreement/DocumentSection';
import { toTemplateDocument } from './RetainerAgreement/templateDocuments';

const APS_FIELD_KEYS = new Set(APS_MATTER_FIELDS.map((field) => field.key));

function getSupportedOverrideValue(isSupported: boolean, value: string): string | null {
    if (!isSupported) {
        return null;
    }

    const trimmed = value.trim();
    return trimmed || null;
}

function getSupportedAmount(isSupported: boolean, value: string): number | null {
    const supported = getSupportedOverrideValue(isSupported, value);
    const amount = supported ? Number.parseFloat(supported) : Number.NaN;
    return Number.isNaN(amount) ? null : amount;
}

interface Props {
    onClose(): void;
    initialMatterType?: ProspectMatterType;
}

export default function NewProspect({ onClose, initialMatterType }: Props) {
    const { activeLawFirmId } = useLawFirmContext();
    const { data: activeLawFirm } = useLawFirm(activeLawFirmId);
    const highlightApsScan = useApsDiscoveryStore((state) => state.highlightApsScan);
    const clearApsScanHighlight = useApsDiscoveryStore((state) => state.clearApsScanHighlight);

    const [isNewProspectLoading, setIsNewProspectLoading] = useState(false);
    const [isCloseAlertOpen, setIsCloseAlertOpen] = useState(false);

    const { data: users = [] } = useUsers({ lawFirmId: activeLawFirmId });

    const { data: retainerAgreements = [] } = useRetainerAgreements(activeLawFirmId);

    // Initial Details state
    const step1Ref = useRef<HTMLDivElement>(null);
    const [customFileNumber, setCustomFileNumber] = useState('');
    const [customFileNumberError, setCustomFileNumberError] = useState('');
    const [practiceArea, setPracticeArea] = useState<ProspectPracticeArea>('RealEstate');
    const [practiceAreaError, setPracticeAreaError] = useState('');
    const [matterType, setMatterType] = useState<ProspectMatterType | ''>('');
    const [matterTypeError, setMatterTypeError] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
    const [selectedTemplateError, setSelectedTemplateError] = useState('');
    const [assignedLawyer, setAssignedLawyer] = useState<number | null>(null);
    const [assignedLawyerError, setAssignedLawyerError] = useState('');
    const [ccPerson, setCcPerson] = useState<string[]>([]);
    const assignedLawyerSignature =
        users.find(({ userId }) => userId === assignedLawyer)?.signature ?? null;

    // Client Details state
    const step2Ref = useRef<HTMLDivElement>(null);
    const [primaryClientDetails, setPrimaryClientDetails] = useState<ClientDetailsEntry>(() =>
        createClientDetailsEntry({ isPrimary: true }),
    );
    const [clientDetails, setClientDetails] = useState<ClientDetailsEntry[]>([]);
    // A client whose automatic ID verification nobody chose follows the firm's default
    const defaultIdvSendTiming = activeLawFirm?.defaultIdvSendTiming ?? null;
    const primaryClient = withDefaultIdvSendTiming(primaryClientDetails, defaultIdvSendTiming);
    const additionalClients = clientDetails.map((client) =>
        withDefaultIdvSendTiming(client, defaultIdvSendTiming),
    );
    const [primaryClientDetailsErrors, setPrimaryClientDetailsErrors] =
        useState<ClientDetailsErrors>({});
    const [clientDetailsErrors, setClientDetailsErrors] = useState<ClientDetailsErrors[]>([]);

    // Email Configuration state
    const step3Ref = useRef<HTMLDivElement>(null);
    const [isEmailRemindersEnabled, setIsEmailRemindersEnabled] = useState<boolean>(true);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailSubjectError, setEmailSubjectError] = useState('');
    const [emailBodyIntroduction, setEmailBodyIntroduction] = useState('');
    const [emailBodyIntroductionError, setEmailBodyIntroductionError] = useState('');
    const [emailBodyClosing, setEmailBodyClosing] = useState('');
    const [emailBodyClosingError, setEmailBodyClosingError] = useState('');

    // Document Section state
    const step4Ref = useRef<HTMLDivElement>(null);
    const [isLegalDocumentEnabled, setIsLegalDocumentEnabled] = useState(true);
    // Raw matter values keyed by `getLegalDocValueKey`, edited by both the APS scan step and the
    // legal document fields. A scan only fills in the values it found. The saved scan, legal doc
    // prefills and first-class columns are all built from these on create.
    const [matterFieldValues, setMatterFieldValues] = useState<Record<string, string>>({});
    // Keyed by legal doc tag.
    const [legalDocPrefillErrors, setLegalDocPrefillErrors] = useState<Record<string, string>>({});
    // Unchanged scan, used to refill client names when Purchase/Sale changes. Edits are applied
    // over it when the prospect is created. The scan row is not updated.
    const [scannedExtraction, setScannedExtraction] = useState<ApsOcrExtraction | null>(null);
    // Values of the last scan applied, kept after the scan is removed, to tell a later scan which
    // values the user never touched.
    const lastScanValuesRef = useRef<Record<string, string>>({});
    const [apsOcrScanId, setApsOcrScanId] = useState<string | null>(null);
    // Files the user uploaded. The template's attachments are added to these on render
    const [uploadedFiles, setUploadedFiles] = useState<UploadedDocument[]>([]);
    const [removedTemplateFileIds, setRemovedTemplateFileIds] = useState<string[]>([]);
    const [selectedRetainerId, setSelectedRetainerId] = useState<number | null>(null);

    // Purchase uses the purchasers as clients. Sale uses the sellers.
    // A new scan, or switching Purchase/Sale, copies those names again.
    useEffect(() => {
        if (!scannedExtraction || (matterType !== 'Purchase' && matterType !== 'Sale')) {
            return;
        }

        const clients = getApsClients(scannedExtraction, matterType);
        setPrimaryClientDetails(createClientDetailsEntry(clients.primary));
        setClientDetails(clients.additional.map((client) => createClientDetailsEntry(client)));
        setPrimaryClientDetailsErrors({});
        setClientDetailsErrors([]);
    }, [scannedExtraction, matterType]);

    // The template's details prefill editable fields below, so don't refetch (and overwrite the
    // user's edits) while the modal is open. Saving a template invalidates these queries
    const quoteTemplateQuery = useQuery({
        ...quoteTemplateQueryOptions(selectedTemplate),
        staleTime: Infinity,
    });
    const quoteTemplate = quoteTemplateQuery.data;

    // The retainer selected for the prospect, from those for its matter that the assigned lawyer
    // can use (their own, or ones for all users), is the first of:
    //   1. the one picked here
    //   2. the template's default (always one for all users)
    //   3. the lawyer's own newest
    //   4. the newest one for all users
    // The API lists them newest first, and the stable sort keeps that within each group
    const matchingLegalDocuments = retainerAgreements
        .filter(
            (retainer) =>
                retainer.practiceArea === practiceArea &&
                retainer.matterType === matterType &&
                (retainer.userId === assignedLawyer || retainer.userId === null),
        )
        .sort((a, b) => Number(a.userId === null) - Number(b.userId === null));
    const templateRetainerId = quoteTemplate?.defaultRetainerAgreementId ?? null;
    const selectedLegalDocument =
        matchingLegalDocuments.find(
            (agreement) => agreement.retainerAgreementId === selectedRetainerId,
        ) ??
        matchingLegalDocuments.find(
            (agreement) => agreement.retainerAgreementId === templateRetainerId,
        ) ??
        matchingLegalDocuments[0];
    const isRealEstate = practiceArea === 'RealEstate';
    const isApsOcrScanAvailable =
        isRealEstate && (matterType === 'Purchase' || matterType === 'Sale');
    // The APS panel only shows its fields while there is a scan. Values it filled stay in
    // `matterFieldValues` after the scan is removed, so only save them while they're shown.
    const hasApsScan = isApsOcrScanAvailable && scannedExtraction !== null;
    const hasCustomFileNumberPlaceholder = Boolean(
        selectedLegalDocument?.placeholders?.includes('custom_file_number'),
    );
    const legalDocTags = getEditableLegalDocTags(selectedLegalDocument?.placeholders, matterType);
    const legalDocValueKeys = new Set(
        legalDocTags.map((tag) => getLegalDocValueKey(tag, matterType)),
    );
    const hasLegalDocumentAmountPlaceholder = legalDocValueKeys.has('retainerAmount');
    const hasSubjectAddressPlaceholder = legalDocValueKeys.has('subjectAddress');
    const hasClosingDatePlaceholder = legalDocValueKeys.has('closingDate');
    const hasPurchasePricePlaceholder = legalDocValueKeys.has('purchasePrice');
    const legalDocValues: Record<string, string> = Object.fromEntries(
        legalDocTags.map((tag) => [
            tag,
            matterFieldValues[getLegalDocValueKey(tag, matterType)] ?? '',
        ]),
    );
    const retainerAmount = (matterFieldValues.retainerAmount ?? '').trim();
    const subjectAddress = (matterFieldValues.subjectAddress ?? '').trim();
    const purchasePrice = (matterFieldValues.purchasePrice ?? '').trim();
    const rawClosingDate = (matterFieldValues.closingDate ?? '').trim();
    const closingDate =
        rawClosingDate && isValidLegalDocPrefillValue('closing_date', rawClosingDate)
            ? rawClosingDate
            : null;
    // Waits for the template, so its retainer is selected before anything is typed into another's
    const isDocumentsStepAvailable = Boolean(matterType && selectedTemplate && quoteTemplate);
    const shouldSendLegalDocument = Boolean(
        isDocumentsStepAvailable && selectedLegalDocument && isLegalDocumentEnabled,
    );
    const isCustomFileNumberRequired = Boolean(
        shouldSendLegalDocument && hasCustomFileNumberPlaceholder,
    );
    // A field is only saved while an input in the modal shows it.
    const isFieldShown = (key: string) =>
        (shouldSendLegalDocument && legalDocValueKeys.has(key)) ||
        (hasApsScan && APS_FIELD_KEYS.has(key));
    const legalDocPrefills = buildLegalDocPrefillsFromValues(
        selectedLegalDocument?.placeholders,
        matterFieldValues,
        matterType,
        { includeOtherPartyNames: hasApsScan },
    );

    const draftRetainerPreview = ((): DraftRetainerPreviewParams => {
        const assignedUser = users.find((user) => user.userId === assignedLawyer);
        const ccEmail = ccPerson[0];
        const ccUser = ccEmail ? users.find((user) => user.email === ccEmail) : undefined;
        const clientName = getSigningName({
            firstName: primaryClientDetails.firstName.trim(),
            lastName: primaryClientDetails.lastName.trim(),
        }).trim();
        const allClientNames = formatAllClientNames([
            {
                firstName: primaryClientDetails.firstName.trim(),
                lastName: primaryClientDetails.lastName.trim(),
            },
            ...clientDetails.map((client) => ({
                firstName: client.firstName.trim(),
                lastName: client.lastName.trim(),
            })),
        ]);
        const parsedRetainerAmount = Number.parseFloat(retainerAmount);

        const preview: DraftRetainerPreviewParams = {
            customFileNumber: customFileNumber.trim() || undefined,
            lawyerName: assignedUser?.fullName,
            lawyerSignature: assignedLawyerSignature ?? undefined,
            lawyerEmail: assignedUser?.email,
            firstCcUserName: ccUser?.fullName,
            firstCcUserEmail: ccUser?.email,
            practiceArea,
            matterType: matterType || undefined,
            clientName: clientName || undefined,
            clientFirstName: primaryClientDetails.firstName.trim() || undefined,
            clientLastName: primaryClientDetails.lastName.trim() || undefined,
            allClientNames: allClientNames || undefined,
            clientEmail: primaryClientDetails.contactEmail.trim() || undefined,
        };

        if (
            hasLegalDocumentAmountPlaceholder &&
            retainerAmount &&
            !Number.isNaN(parsedRetainerAmount)
        ) {
            preview.retainerAmount = retainerAmount;
        }
        if (hasSubjectAddressPlaceholder && subjectAddress) {
            preview.subjectAddress = subjectAddress;
        }
        if (hasClosingDatePlaceholder && closingDate) {
            preview.closingDate = closingDate;
        }
        if (hasPurchasePricePlaceholder && purchasePrice) {
            preview.purchasePrice = purchasePrice;
        }

        if (Object.keys(legalDocPrefills).length > 0) {
            preview.legalDocPrefills = legalDocPrefills;
        }

        return preview;
    })();

    const { data: quoteTemplates = [] } = useTemplates(activeLawFirmId);

    const filteredQuoteTemplates = useMemo(
        () =>
            quoteTemplates.filter(
                (template) =>
                    template.templateType === 'IntakeOnly' &&
                    template.process === matterType &&
                    template.templateForms.some(
                        (tf) => tf.templateFormType === 'IntakeForm' && tf.status === 'Completed',
                    ),
            ),
        [quoteTemplates, matterType],
    );

    // Like the template above, these prefill editable fields, so they don't refetch while the
    // modal is open
    const intakeEmailTemplateQuery = useQuery({
        ...intakeEmailTemplateQueryOptions(selectedTemplate),
        staleTime: Infinity,
    });
    const intakeEmailReminderTemplateQuery = useQuery({
        ...intakeEmailReminderTemplateQueryOptions(selectedTemplate),
        staleTime: Infinity,
    });
    const templateAttachmentsQuery = useQuery({
        ...quoteTemplateAttachmentsQueryOptions(selectedTemplate),
        staleTime: Infinity,
    });
    const intakeEmailTemplate = intakeEmailTemplateQuery.data;
    const intakeEmailReminderTemplate = intakeEmailReminderTemplateQuery.data;
    const templateAttachments = templateAttachmentsQuery.data;
    const isSelectedTemplateDetailsLoading =
        quoteTemplateQuery.isFetching ||
        intakeEmailTemplateQuery.isFetching ||
        intakeEmailReminderTemplateQuery.isFetching ||
        templateAttachmentsQuery.isFetching;

    const resolveTemplateCcEmails = useCallback(
        (emailCC: string[] | null | undefined) => {
            const userEmailByLowerCase = new Map(
                users.map((user) => [user.email.toLowerCase(), user.email]),
            );

            return (emailCC ?? []).map(
                (email) => userEmailByLowerCase.get(email.toLowerCase()) ?? email,
            );
        },
        [users],
    );

    useEffect(() => {
        if (!quoteTemplate || !intakeEmailTemplate || !intakeEmailReminderTemplate) return;

        setAssignedLawyer(quoteTemplate.quoteApproverId || null);
        setEmailSubject(intakeEmailTemplate.emailSubject || '');
        setEmailBodyIntroduction(intakeEmailTemplate.emailBodyIntroduction || '');
        setEmailBodyClosing(intakeEmailTemplate.emailBodyClosing || '');
        // Prefer the template's default CCs (set with the assigned user) over the intake email snapshot.
        setCcPerson(resolveTemplateCcEmails(quoteTemplate.emailCC ?? intakeEmailTemplate.emailCC));
        setIsEmailRemindersEnabled(!!intakeEmailReminderTemplate.isQuoteReminderOn);
    }, [quoteTemplate, intakeEmailTemplate, intakeEmailReminderTemplate, resolveTemplateCcEmails]);

    // The selected template's attachments prefill the documents. The user can remove them
    const templateDocuments = (templateAttachments ?? [])
        .filter(({ fileId }) => !removedTemplateFileIds.includes(fileId))
        .map(toTemplateDocument);
    const documents = [...templateDocuments, ...uploadedFiles];

    const handleDocumentsChange = (files: UploadedDocument[]) => {
        const keptFileIds = new Set(files.map(({ id }) => id));
        setRemovedTemplateFileIds((current) => [
            ...current,
            ...templateDocuments.map(({ id }) => id).filter((fileId) => !keptFileIds.has(fileId)),
        ]);
        setUploadedFiles(files.filter(({ isFromTemplate }) => !isFromTemplate));
    };

    const getCustomFileNumberError = useCallback(
        (value: string) => {
            if (value.length > 400) {
                return 'Custom file number must be less than 400 characters.';
            }
            if (isCustomFileNumberRequired && !value.trim()) {
                return 'Custom file number is required.';
            }
            return '';
        },
        [isCustomFileNumberRequired],
    );

    const handleFinish = async () => {
        const nextCustomFileNumberError = getCustomFileNumberError(customFileNumber);
        const nextPracticeAreaError = !practiceArea ? 'Practice area is required.' : '';
        const nextMatterTypeError = !matterType ? 'Matter type is required.' : '';
        const nextSelectedTemplateError = !selectedTemplate ? 'Select Template is required.' : '';
        const nextAssignedLawyerError = !assignedLawyer ? 'Assigned user is required.' : '';
        const nextEmailSubjectError = !emailSubject.trim() ? 'Email subject is required.' : '';
        const nextEmailBodyIntroductionError = !emailBodyIntroduction.trim()
            ? 'Email body introduction is required.'
            : '';
        const nextEmailBodyClosingError = !emailBodyClosing.trim()
            ? 'Email body closing is required.'
            : '';

        const nextPrimaryClientDetailsErrors = getClientDetailsErrors(primaryClient);
        const nextClientDetailsErrors = additionalClients.map(getClientDetailsErrors);

        const allEmails = [
            primaryClientDetails.contactEmail.toLowerCase().trim(),
            ...clientDetails.map((c) => c.contactEmail.toLowerCase().trim()),
        ].filter(Boolean);
        const duplicateEmails = new Set(
            allEmails.filter((email, index) => allEmails.indexOf(email) !== index),
        );

        if (duplicateEmails.has(primaryClientDetails.contactEmail.toLowerCase().trim())) {
            nextPrimaryClientDetailsErrors.contactEmail = 'Duplicate email. Must be unique.';
        }

        clientDetails.forEach((client, index) => {
            if (duplicateEmails.has(client.contactEmail.toLowerCase().trim())) {
                nextClientDetailsErrors[index].contactEmail = 'Duplicate email. Must be unique.';
            }
        });

        const nextLegalDocPrefillErrors: Record<string, string> = {};
        if (shouldSendLegalDocument) {
            for (const tag of legalDocTags) {
                const value = legalDocValues[tag]?.trim();
                if (value && isValidLegalDocPrefillValue(tag, value)) {
                    continue;
                }
                const label = getLegalDocPrefillLabel(tag).toLowerCase();
                const article = value ? 'a valid' : 'a';
                nextLegalDocPrefillErrors[tag] =
                    getLegalDocPrefillWidget(tag) === 'date'
                        ? `Please select ${article} ${label}`
                        : `Please enter ${article} ${label}`;
            }
        }

        setCustomFileNumberError(nextCustomFileNumberError);
        setPracticeAreaError(nextPracticeAreaError);
        setMatterTypeError(nextMatterTypeError);
        setSelectedTemplateError(nextSelectedTemplateError);
        setAssignedLawyerError(nextAssignedLawyerError);
        setPrimaryClientDetailsErrors(nextPrimaryClientDetailsErrors);
        setClientDetailsErrors(nextClientDetailsErrors);
        setEmailSubjectError(nextEmailSubjectError);
        setEmailBodyIntroductionError(nextEmailBodyIntroductionError);
        setEmailBodyClosingError(nextEmailBodyClosingError);
        setLegalDocPrefillErrors(nextLegalDocPrefillErrors);

        if (
            nextCustomFileNumberError ||
            nextPracticeAreaError ||
            nextMatterTypeError ||
            nextSelectedTemplateError ||
            nextAssignedLawyerError
        ) {
            return step1Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (
            hasClientDetailsErrors(nextPrimaryClientDetailsErrors) ||
            nextClientDetailsErrors.some(hasClientDetailsErrors)
        ) {
            return step2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (
            nextEmailSubjectError ||
            nextEmailBodyIntroductionError ||
            nextEmailBodyClosingError
        ) {
            return step3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (Object.values(nextLegalDocPrefillErrors).some(Boolean)) {
            return step4Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (isDocumentsStepAvailable && documents.length > MAX_ADDITIONAL_DOCUMENTS) {
            // Switching templates can add the new template's documents to the uploaded ones
            errorSnackbar(`Please remove documents to send at most ${MAX_ADDITIONAL_DOCUMENTS}`);
            return step4Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        try {
            setIsNewProspectLoading(true);
            await createProspectManually({
                quoteTemplateId: selectedTemplate as number,
                approverId: assignedLawyer as number,
                contacts: [
                    toCreateContact({ ...primaryClient, isPrimary: true }),
                    ...additionalClients.map(toCreateContact),
                ],
                intakeEmailSignature: assignedLawyerSignature,
                intakeCCEmails: ccPerson,
                intakeEmailSubject: emailSubject,
                intakeEmailIntroduction: emailBodyIntroduction,
                intakeEmailClosing: emailBodyClosing,
                isIntakeReminderEnable: isEmailRemindersEnabled,
                customFileNumber,
                isRetainerEnable: shouldSendLegalDocument,
                retainerAgreementId: shouldSendLegalDocument
                    ? (selectedLegalDocument?.retainerAgreementId ?? null)
                    : null,
                retainerAmount: getSupportedAmount(isFieldShown('retainerAmount'), retainerAmount),
                subjectAddress: getSupportedOverrideValue(
                    isFieldShown('subjectAddress'),
                    subjectAddress,
                ),
                closingDate: isFieldShown('closingDate') ? closingDate : null,
                legalDocPrefills,
                purchasePrice: getSupportedAmount(isFieldShown('purchasePrice'), purchasePrice),
                apsOcrExtraction: hasApsScan
                    ? applyFieldValuesToExtraction(scannedExtraction, matterFieldValues)
                    : null,
                apsOcrScanId: hasApsScan ? apsOcrScanId : null,
                attachmentFileIds: isDocumentsStepAvailable ? documents.map(({ id }) => id) : [],
            });
            onClose();
        } catch (error) {
            console.error(error);
            errorSnackbar('Something went wrong. Please try again.');
        } finally {
            setIsNewProspectLoading(false);
        }
    };

    useEffect(() => {
        const assignedLawyerEmail = users.find((user) => user.userId === assignedLawyer)?.email;

        if (!assignedLawyerEmail) return;

        const assignedLawyerEmailLower = assignedLawyerEmail.toLowerCase();
        if (!ccPerson.some((email) => email.toLowerCase() === assignedLawyerEmailLower)) return;

        setCcPerson((prev) =>
            prev.filter((email) => email.toLowerCase() !== assignedLawyerEmailLower),
        );
    }, [assignedLawyer, ccPerson, users]);

    const resetTemplateDerivedFields = useCallback(() => {
        setEmailSubject('');
        setEmailBodyIntroduction('');
        setEmailBodyClosing('');
        setCcPerson([]);
        setIsEmailRemindersEnabled(true);
    }, []);

    const clearLegalDocumentFieldErrors = useCallback(() => {
        setLegalDocPrefillErrors({});
    }, []);

    const clearApsScanDraft = useCallback(() => {
        setScannedExtraction(null);
        setApsOcrScanId(null);
    }, []);

    const resetErrors = useCallback(() => {
        setCustomFileNumberError('');
        setPracticeAreaError('');
        setMatterTypeError('');
        setSelectedTemplateError('');
        setAssignedLawyerError('');
        setPrimaryClientDetailsErrors({});
        setClientDetailsErrors([]);
        setEmailSubjectError('');
        setEmailBodyIntroductionError('');
        setEmailBodyClosingError('');
        clearLegalDocumentFieldErrors();
    }, [clearLegalDocumentFieldErrors]);

    const handleCustomFileNumberChange = useCallback(
        (value: string) => {
            setCustomFileNumber(value);
            setCustomFileNumberError(getCustomFileNumberError(value));
        },
        [getCustomFileNumberError],
    );

    const handlePracticeAreaChange = useCallback(
        (value: ProspectPracticeArea) => {
            setPracticeArea(value);
            setMatterType('');
            setSelectedTemplate(null);
            setAssignedLawyer(null);
            resetTemplateDerivedFields();
            resetErrors();
            clearApsScanDraft();
        },
        [resetTemplateDerivedFields, resetErrors, clearApsScanDraft],
    );

    const handleMatterTypeChange = useCallback(
        (value: ProspectMatterType) => {
            setMatterType(value);
            setSelectedTemplate(null);
            setAssignedLawyer(null);
            resetTemplateDerivedFields();
            resetErrors();
            if (value !== 'Purchase' && value !== 'Sale') {
                clearApsScanDraft();
            }
        },
        [resetTemplateDerivedFields, resetErrors, clearApsScanDraft],
    );

    useEffect(() => {
        if (!initialMatterType) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setMatterType((current) => current || initialMatterType);
        }, 1500);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [initialMatterType]);

    const handleSelectedTemplateChange = useCallback(
        (quoteTemplateId: number | null) => {
            setSelectedTemplate(quoteTemplateId);
            setRemovedTemplateFileIds([]);
            setSelectedRetainerId(null);
            resetTemplateDerivedFields();

            const selectedQuoteTemplate = filteredQuoteTemplates.find(
                (template) => template.quoteTemplateId === quoteTemplateId,
            );

            setAssignedLawyer(selectedQuoteTemplate?.quoteApproverId ?? null);
            setCcPerson(resolveTemplateCcEmails(selectedQuoteTemplate?.emailCC));
            setSelectedTemplateError('');
            setAssignedLawyerError('');
            setEmailSubjectError('');
            setEmailBodyIntroductionError('');
            setEmailBodyClosingError('');
            clearLegalDocumentFieldErrors();
        },
        [
            filteredQuoteTemplates,
            resolveTemplateCcEmails,
            resetTemplateDerivedFields,
            clearLegalDocumentFieldErrors,
        ],
    );

    const handleAssignedLawyerChange = useCallback(
        (value: number | null) => {
            setAssignedLawyer(value);
            setAssignedLawyerError('');
            clearLegalDocumentFieldErrors();
        },
        [clearLegalDocumentFieldErrors],
    );

    const handleSelectedRetainerChange = useCallback(
        (value: number) => {
            setSelectedRetainerId(value);
            clearLegalDocumentFieldErrors();
        },
        [clearLegalDocumentFieldErrors],
    );

    const addClientRow = useCallback(() => {
        setClientDetails((prev) => [...prev, createClientDetailsEntry()]);
    }, []);

    const handleScannedExtractionChange = useCallback((extraction: ApsOcrExtraction | null) => {
        setScannedExtraction(extraction);
        if (!extraction) {
            return;
        }

        const previousScanValues = lastScanValuesRef.current;
        const scanValues = getApsFieldValues(extraction);
        lastScanValuesRef.current = scanValues;
        setMatterFieldValues((current) =>
            mergeScanFieldValues(current, previousScanValues, scanValues),
        );
    }, []);

    const handleMatterFieldChange = useCallback((key: string, value: string) => {
        setMatterFieldValues((current) => ({ ...current, [key]: value }));
    }, []);

    const handleLegalDocValueChange = useCallback(
        (tag: string, value: string) => {
            handleMatterFieldChange(getLegalDocValueKey(tag, matterType), value);
            setLegalDocPrefillErrors((current) => ({ ...current, [tag]: '' }));
        },
        [matterType, handleMatterFieldChange],
    );

    const handleEmailSubjectChange = useCallback((value: string) => {
        setEmailSubject(value);
        setEmailSubjectError(value.trim() ? '' : 'Email subject is required.');
    }, []);

    const handleEmailBodyIntroductionChange = useCallback((value: string) => {
        setEmailBodyIntroduction(value);
        setEmailBodyIntroductionError(value.trim() ? '' : 'Email body introduction is required.');
    }, []);

    const handleEmailBodyClosingChange = useCallback((value: string) => {
        setEmailBodyClosing(value);
        setEmailBodyClosingError(value.trim() ? '' : 'Email body closing is required.');
    }, []);

    return (
        <>
            <Dialog
                open
                maxWidth={false}
                PaperProps={{
                    onClickCapture: highlightApsScan ? clearApsScanHighlight : undefined,
                    sx: {
                        borderRadius: '10px',
                        width: '90vW',
                    },
                }}
            >
                <Stack
                    sx={{
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        color: 'white',
                        padding: '16px 20px',
                        textAlign: 'center',
                    }}
                >
                    <DialogTitle
                        sx={{
                            fontSize: '20px',
                            fontWeight: 500,
                            marginBottom: '4px',
                            paddingBottom: '0px',
                        }}
                    >
                        Create New Prospect
                    </DialogTitle>
                    <DialogTitle
                        sx={{
                            opacity: 0.9,
                            fontSize: '13px',
                            padding: '0px',
                        }}
                    >
                        Set up a new transaction with client details and document configuration
                    </DialogTitle>
                </Stack>
                <DialogContent sx={THIN_SCROLLBAR_STYLING}>
                    <Stack spacing={3}>
                        <NewProspectAnimatedStep stepRef={step1Ref}>
                            <NpInitialDetails
                                users={users}
                                filteredQuoteTemplates={filteredQuoteTemplates}
                                lawFirmId={activeLawFirmId}
                                isTestFirm={activeLawFirm?.isTestFirm ?? false}
                                customFileNumber={customFileNumber}
                                handleCustomFileNumberChange={handleCustomFileNumberChange}
                                customFileNumberError={customFileNumberError}
                                isCustomFileNumberRequired={isCustomFileNumberRequired}
                                practiceArea={practiceArea}
                                handlePracticeAreaChange={handlePracticeAreaChange}
                                practiceAreaError={practiceAreaError}
                                matterType={matterType}
                                handleMatterTypeChange={handleMatterTypeChange}
                                matterTypeError={matterTypeError}
                                selectedTemplate={selectedTemplate}
                                handleSelectedTemplateChange={handleSelectedTemplateChange}
                                selectedTemplateError={selectedTemplateError}
                                isTemplateDetailsLoading={isSelectedTemplateDetailsLoading}
                                assignedLawyer={assignedLawyer}
                                handleAssignedLawyerChange={handleAssignedLawyerChange}
                                assignedLawyerError={assignedLawyerError}
                                ccPerson={ccPerson}
                                setCcPerson={setCcPerson}
                                resetErrors={resetErrors}
                            />
                        </NewProspectAnimatedStep>
                        <NewProspectAnimatedStep show={isApsOcrScanAvailable}>
                            <ApsOcrScan
                                scannedExtraction={scannedExtraction}
                                onScannedExtractionChange={handleScannedExtractionChange}
                                fieldValues={matterFieldValues}
                                onFieldChange={handleMatterFieldChange}
                                onApsScanIdChange={setApsOcrScanId}
                                highlighted={highlightApsScan}
                            />
                        </NewProspectAnimatedStep>
                        <NewProspectAnimatedStep stepRef={step2Ref}>
                            <ClientDetails
                                primaryClientDetails={primaryClient}
                                setPrimaryClientDetails={setPrimaryClientDetails}
                                primaryClientDetailsErrors={primaryClientDetailsErrors}
                                clientDetails={additionalClients}
                                setClientDetails={setClientDetails}
                                clientDetailsErrors={clientDetailsErrors}
                                defaultIdvSendTiming={defaultIdvSendTiming}
                                addClientRow={addClientRow}
                                resetErrors={resetErrors}
                            />
                        </NewProspectAnimatedStep>
                        <NewProspectAnimatedStep stepRef={step3Ref}>
                            <EmailConfiguration
                                selectedTemplate={selectedTemplate}
                                isTemplateDetailsLoading={isSelectedTemplateDetailsLoading}
                                primaryClientFirstName={primaryClientDetails.firstName}
                                isEmailRemindersEnabled={isEmailRemindersEnabled}
                                setIsEmailRemindersEnabled={setIsEmailRemindersEnabled}
                                emailSubject={emailSubject}
                                emailSubjectError={emailSubjectError}
                                handleEmailSubjectChange={handleEmailSubjectChange}
                                emailBodyIntroduction={emailBodyIntroduction}
                                emailBodyIntroductionError={emailBodyIntroductionError}
                                handleEmailBodyIntroductionChange={
                                    handleEmailBodyIntroductionChange
                                }
                                emailBodyClosing={emailBodyClosing}
                                emailBodyClosingError={emailBodyClosingError}
                                handleEmailBodyClosingChange={handleEmailBodyClosingChange}
                                signature={assignedLawyerSignature}
                                resetErrors={resetErrors}
                            />
                        </NewProspectAnimatedStep>
                        <NewProspectAnimatedStep show={isDocumentsStepAvailable} stepRef={step4Ref}>
                            <RetainerAgreement
                                matchingRetainers={matchingLegalDocuments}
                                retainerAgreement={selectedLegalDocument}
                                selectedRetainerId={
                                    selectedLegalDocument?.retainerAgreementId ?? null
                                }
                                onSelectRetainer={handleSelectedRetainerChange}
                                draftRetainerPreview={draftRetainerPreview}
                                isRetainerAgreementEnabled={isLegalDocumentEnabled}
                                setIsRetainerAgreementEnabled={setIsLegalDocumentEnabled}
                                legalDocTags={legalDocTags}
                                legalDocValues={legalDocValues}
                                legalDocErrors={legalDocPrefillErrors}
                                onLegalDocValueChange={handleLegalDocValueChange}
                                documents={documents}
                                onDocumentsChange={handleDocumentsChange}
                                templateName={quoteTemplate?.quoteTemplateName}
                                templateRetainerId={templateRetainerId}
                                resetErrors={resetErrors}
                            />
                        </NewProspectAnimatedStep>
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        gap: '10px',
                        justifyContent: 'flex-end',
                        padding: '16px 20px',
                        background: '#f8f9fa',
                        borderTop: '1px solid #e8eaed',
                    }}
                >
                    <Button variant="outlined" onClick={() => setIsCloseAlertOpen(true)}>
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={isNewProspectLoading}
                        disabled={isSelectedTemplateDetailsLoading}
                        variant="contained"
                        onClick={handleFinish}
                        autoFocus
                    >
                        Create Prospect
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <AlertDialog
                open={isCloseAlertOpen}
                handleClose={() => setIsCloseAlertOpen(false)}
                handleConfirm={onClose}
                title="Are you sure?"
                message="Closing the popup will result in loss of any data that you have filled up. Are you sure you want to close the popup?"
            />
        </>
    );
}
